import moment from 'moment';
import React, { Component } from 'react';
import {
  Alert,
  ControlLabel,
  Button,
  FormGroup,
  Modal,
  Row,
  Col,
  FormControl,
} from 'react-bootstrap';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { getLocationUpdates } from 'components/modules/booking/BookingHelper';
import { intentMap } from 'utils/formatters/stops';
import stopIntentTypes from 'config/stop-intent-types';
import CargomaticInput from 'components/v2/input';
import Location from 'components/modules/booking/Location';
import LocationContact from 'components/modules/booking/LocationContact';
import TimeWindow from 'components/time/TimeWindow';
import XIcon from 'components/v2/icons/XIcon';

import { sluggify } from 'utils/localization/utils';
import { Tab } from '@headlessui/react';
import LoadDetailsForm from 'components/load/LoadDetailsForm';
import { defaultLoad } from 'utils/localization/types';
import { withLocaleContext } from 'providers/locale-settings-context';
import Tabs from 'components/v2/navs/Tabs';
import Checkbox from 'components/v2/forms/Checkbox';
import { validateLoadItem } from 'helpers/booking/validation';
import { isDrayageShipment } from 'helpers/shipment-types';
import { isProd } from 'helpers/helper_functions';
import AlertComponent from 'components/ui/Alert';
import YardStopForm from './YardStopForm';

function getTimeWindow(timezone) {
  const now = moment();

  let windowNow = now.clone();
  if (timezone) {
    windowNow = windowNow.tz(timezone);
  }
  const windowStart = windowNow.add('minutes', 30 - (now.minute() % 30));

  const window_start = windowStart.toISOString();
  const window_end = windowStart.add('hours', 2).toISOString();

  return {
    window_start,
    window_end,
  };
}

function getNewLoad(defaultLoadData) {
  return {
    ...defaultLoadData,
  };
}

function getInitialState() {
  return {
    error: {},
    action: '',
    location: {
      name: '',
      address: {
        label: '',
      },
      lat: null,
      lng: null,
      suite_or_apt: '',
    },
    loads: [],
    contact: {},
    off_site: false,
    full_truckload: false,
    ...getTimeWindow(),
  };
}

const getTabs = (t, isDrayageShipment) => {
  return [
    {
      name: <div data-testid="addShipmentStop">{t('ADD_SHIPMENT_STOP')}</div>,
    },
    ...(isDrayageShipment
      ? [
          {
            name: <div data-testid="addYardtStop">{t('ADD_YARD_STOP')}</div>,
          },
        ]
      : []),
  ];
};

class AddStopModal extends Component {
  static propTypes = {
    show: PropTypes.bool,
    shipment: PropTypes.shape({
      stops: PropTypes.arrayOf(
        PropTypes.shape({
          action: PropTypes.string,
        }),
      ),
    }).isRequired,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
  };

  static defaultPropTypes = {
    show: false,
  };

  constructor(props) {
    super(props);
    this.state = getInitialState();
    this.isDrayageShipment = isDrayageShipment(this.props.shipment);
  }

  getContact() {
    return this.state.contact;
  }

  getValidationState(key) {
    const { error } = this.state;
    if (Object.prototype.hasOwnProperty.call(error, key)) {
      return 'error';
    }
    return null;
  }

  handleActionChange = (e) => {
    this.setState({
      action: e.target.value,
    });
  };

  addLoad = (newLoads) => {
    this.setState({ loads: newLoads });
  };

  handleRemoveLoad = (newLoads, index) => {
    this.setState({ loads: newLoads });
  };

  handleLoadFieldChange = (newLoads, field, value, index) => {
    this.setState({ loads: newLoads });
  };

  handleToggleFullTruckLoad = (full_truckload) => {
    const localeData = this.props.localeData;
    this.setState(() => {
      if (full_truckload) {
        return {
          full_truckload,
          loads: [],
        };
      }
      return {
        full_truckload: false,
        loads: [
          getNewLoad({
            ...defaultLoad,
            ...localeData,
          }),
        ],
      };
    }, this.requestQuote);
  };

  handleLocationFieldChange = (field, value) => {
    this.setState((prevState) => {
      const location = {
        ...prevState.location,
        [field]: value,
      };
      return {
        ...prevState,
        location,
      };
    });
  };
  // Locations
  handleLocationNameSelect = (selectedLocation) => {
    const location = getLocationUpdates(selectedLocation);

    this.setState({ location });
  };

  handleLocationAddressSelect = (item) => {
    const { label, location } = item;
    const { lat, lng } = location;
    const coords = [lng, lat];
    this.setState((prevState) => {
      const locationData = Object.assign(
        { ...prevState.location },
        {
          address: {
            label,
            location,
          },
          coords,
        },
      );
      return {
        ...prevState,
        location: locationData,
      };
    });
  };

  handleLocationContactChange = (attribute, value) => {
    const contact = { ...this.getContact() };
    contact[attribute] = value;
    this.setState({ contact });
  };

  handleTimeWindowOpenChange = (timeStamp) => {
    this.setState({ window_start: timeStamp });
  };

  handleTimeWindowCloseChange = (timeStamp) => {
    this.setState({ window_end: timeStamp });
  };

  handleOffSiteChange = (off_site) => {
    this.setState((prevState) => {
      return {
        ...prevState,
        off_site,
      };
    });
  };
  handleSaveClick = () => {
    const error = this.validate();
    if (Object.keys(error).length === 0) {
      const {
        location: {
          address: { label: address },
          name,
          suite_or_apt,
          lat,
          lng,
        },
        window_start,
        window_end,
        loads,
        off_site,
      } = this.state;
      const stop = {
        window_start,
        window_end,
        action: intentMap[this.state.action].action,
        empty: intentMap[this.state.action].empty,
        location: {
          address,
          name,
          suite_or_apt,
          lat,
          lng,
        },
        loads,
        off_site,
      };

      this.props.onSave(stop);
    }
  };

  validate() {
    const error = {};

    const {
      action,
      loads,
      location: { name, address },
    } = this.state;
    const { t } = this.props;
    if (!action) {
      error.action = t('INTENT_IS_REQUIRED_ERROR');
    } else {
      const {
        shipment: { stops },
      } = this.props;
      const otherActions = stops.map((s) => s.action);
      const actions = [...otherActions, action];
      if (!actions.includes('pickup') || !actions.includes('deliver')) {
        error.action = t('MINIMUM_STOP_ERROR');
      }
    }

    if (!name) {
      error.name = t('LOCATION_IS_REQUIRED_ERROR');
    }

    if (!address || !address.label) {
      error.address = t('ADDRESS_FROM_LIST_ERROR');
    }

    let loadValid = true;
    let loadErrors = [];
    loads.forEach((load) => {
      const loadError = validateLoadItem({ ...load, t });
      if (Object.keys(loadError).length > 0) {
        loadValid = false;
        loadErrors = loadErrors.concat(Object.values(loadError));
      }
    });

    if (!loadValid) {
      error.loads = loadErrors[0];
    }

    this.setState({ error });
    return error;
  }

  renderIntentDropdown() {
    const { t } = this.props;
    const { action } = this.state;

    return (
      <FormGroup validationState={this.getValidationState('action')}>
        <FormControl componentClass="select" onChange={this.handleActionChange}>
          <option value="">{t('SELECT_INTENT')}</option>
          {stopIntentTypes.map((item) => {
            const selected = action === item.slug;

            return (
              <option key={item.slug} value={item.slug} selected={selected}>
                {t(item.localizationSlug)}
              </option>
            );
          })}
        </FormControl>
      </FormGroup>
    );
  }

  renderErrors() {
    const { error } = this.state;
    const errorKeys = Object.keys(error);
    const hasErrors = errorKeys.length > 0;
    const { t, shipment } = this.props;

    if (hasErrors) {
      return (
        <Alert bsStyle="danger">
          <ul>
            {errorKeys.map((key) => (
              <li>{error[key]}</li>
            ))}
          </ul>
        </Alert>
      );
    }

    if (shipment.carrier && shipment.carrier.portpro) {
      const {
        carrier: {
          portpro: { enabled: portProEnabled },
        },
      } = shipment;

      if (portProEnabled) {
        return (
          <AlertComponent
            className="mt-2"
            type="warning"
            title={t('ADD_STOP_PORTPRO_WARNING')}
          />
        );
      }
    }

    return null;
  }

  renderShipmentStopTab() {
    const { t } = this.props;
    const { action, location } = this.state;

    const label = t(sluggify(action || ''));
    const contact = this.getContact();

    return (
      <div className="add-stop__form">
        <Row>
          <Col md={12}>{this.renderErrors()}</Col>
        </Row>
        <fieldset>
          <legend>{t('LOCATION_INTENT')}</legend>
          <Row>
            <Col md={3}>
              <Row>
                <Col md={12}>
                  <ControlLabel>{t('SELECT_INTENT')}</ControlLabel>
                  {this.renderIntentDropdown()}
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Checkbox
                    id="off_site_checkbox"
                    checked={this.state.off_site}
                    onChange={this.handleOffSiteChange}
                  >
                    {t('IS_OFFSITE')}
                  </Checkbox>
                </Col>
              </Row>
            </Col>
            <Col md={9}>
              <Location
                label={label}
                onChange={this.handleLocationFieldChange}
                onNameSelect={this.handleLocationNameSelect}
                onAddressSelect={this.handleLocationAddressSelect}
                location={location}
                onRemoveLocation={() => {}}
              />
              <LocationContact
                contact={contact}
                onContactChange={this.handleLocationContactChange}
              />
            </Col>
          </Row>
        </fieldset>
        <fieldset>
          <legend>{t('TIME_WINDOW')}</legend>
          <Row>
            <Col md={9} mdOffset={3}>
              <FormGroup>
                <TimeWindow
                  startTimeLabel={t('READY_TIME')}
                  open={this.state.window_start}
                  close={this.state.window_end}
                  onWindowOpenChange={this.handleTimeWindowOpenChange}
                  onWindowCloseChange={this.handleTimeWindowCloseChange}
                />
              </FormGroup>
            </Col>
          </Row>
        </fieldset>
        <fieldset>
          <legend>{t('LOAD_DETAILS')}</legend>
          <Row className="form-row">
            <Col md={12}>
              <CargomaticInput
                className="my-4"
                type="checkbox"
                id="full_truckload"
                label={t('FULL_TRUCKLOAD')}
                info={t('FULL_TRUCKLOAD_NOTE')}
                value={this.state.full_truckload}
                onChange={this.handleToggleFullTruckLoad}
              />
              <LoadDetailsForm
                loadOnly
                loads={this.state.loads}
                fullTruckload={this.state.full_truckload}
                addLoad={this.addLoad}
                onRemoveLoad={this.handleRemoveLoad}
                onLoadFieldChange={this.handleLoadFieldChange}
                onTotalShipmentWeightChange={() => {}}
              />
            </Col>
          </Row>
        </fieldset>

        <div className="flex justify-end gap-3">
          <Button
            onClick={() => {
              this.setState(getInitialState());
              this.props.onClose();
            }}
          >
            {t('CANCEL')}
          </Button>
          <Button bsStyle="primary" onClick={this.handleSaveClick}>
            {titleize(t('SAVE'))}
          </Button>
        </div>
      </div>
    );
  }

  renderYardStopsTab() {
    return (
      <YardStopForm
        shipment={this.props.shipment}
        onClose={() => {
          this.setState(getInitialState());
          this.props.onClose();
        }}
      />
    );
  }

  render() {
    const { t } = this.props;

    return (
      <Modal data-testid="addStopModal" bsSize="large" show={this.props.show}>
        <Modal.Body data-testid="addStopModalBody" className="relative">
          <Tabs tabs={getTabs(t, this.isDrayageShipment)}>
            <Tab.Panel>
              <div data-testid="addShipmentStopBody">
                {this.renderShipmentStopTab()}
              </div>
            </Tab.Panel>
            <Tab.Panel>
              {this.isDrayageShipment && (
                <div data-testid="addYardStopBody">
                  {this.renderYardStopsTab()}
                </div>
              )}
            </Tab.Panel>
          </Tabs>
          <button
            className="absolute right-6 top-6"
            onClick={this.props.onClose}
          >
            <XIcon />
          </button>
        </Modal.Body>
      </Modal>
    );
  }
}
export default withTranslation()(withLocaleContext(AddStopModal));
